import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { BookmarkPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog that promotes a saved estimate's component+milestone shape into a
 * named, reusable scope template stored in `/api/scope-templates`. Strips
 * customer-specific fields server-side.
 *
 * Props:
 *  open                boolean
 *  onClose()
 *  sourceEstimateId    string  (required — must be a persisted estimate)
 *  defaultName         string  (suggested template name, e.g. the estimate's name)
 *  onSaved(template)   optional callback after a successful save
 */
export function SaveAsTemplateDialog({ open, onClose, sourceEstimateId, defaultName = '', onSaved }) {
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [includeModifiers, setIncludeModifiers] = useState(true);
  const [saving, setSaving] = useState(false);

  // Sync default name whenever the dialog re-opens for a new source
  React.useEffect(() => {
    if (open) {
      setName(defaultName || '');
      setDescription('');
      setIncludeModifiers(true);
    }
  }, [open, defaultName]);

  const handleSave = async () => {
    if (!name.trim()) return;
    if (!sourceEstimateId) {
      toast.error('Save the estimate first before promoting it to a template.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/scope-templates`,
        {
          name: name.trim(),
          description: description.trim() || null,
          source_estimate_id: sourceEstimateId,
          include_modifiers: includeModifiers,
        },
        { withCredentials: true }
      );
      toast.success(`Saved "${res.data.name}" — anyone in your workspace can now apply it from Step 1.`);
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not save template';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="save-as-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
            <BookmarkPlus className="w-4 h-4 text-cyan-600" />
            Save as Scope Template
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Promote this estimate&apos;s components and milestone deliverables into a reusable template.
            Customer details (org, names, results) are stripped automatically — only the scope shape is kept.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="tpl-name" className="text-xs">Template name</Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Standard Mid-Market LMS Rollout"
              className="mt-1"
              data-testid="save-template-name-input"
              autoFocus
              disabled={saving}
            />
          </div>
          <div>
            <Label htmlFor="tpl-desc" className="text-xs">Description <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When this template should be used, who it suits, etc."
              className="mt-1 text-xs min-h-[60px]"
              data-testid="save-template-description-input"
              disabled={saving}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeModifiers}
              onChange={(e) => setIncludeModifiers(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#0F2040]"
              data-testid="save-template-include-modifiers"
              disabled={saving}
            />
            Include the readiness/complexity modifiers from this estimate
          </label>
        </div>

        <DialogFooter className="mt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={saving}
            data-testid="save-template-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving || !name.trim() || !sourceEstimateId}
            className="bg-[#0F2040] hover:bg-[#0F2040]/90"
            data-testid="save-template-save-btn"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
            {saving ? 'Saving...' : 'Save template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SaveAsTemplateDialog;
